import { Box } from '@mui/material';
import React, { useMemo, useRef } from 'react';

import useSWR from 'swr';
import { SurfTooltipData } from 'types/graphs';
import { TideData } from 'types/tide';
import { Units } from 'types/units';
import { WindData } from 'types/wind';
import { useRouter } from 'next/router';
import { doFetch } from 'common/baseFetch';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import LoadingSpinner from 'components/CamPlayerKbygRewinds/LoadingSpinner/LoadingSpinner';
import { convertToSwellSigRequest, SwellSigRequest } from './convertToSwellSigRequest';
import styles from './SwellSignaturePoc.module.scss';
import { CamThumbImage } from './CamThumbImage';

const SURF_CARD_PADDING = 20;
interface ForecastMatches {
  distances: number[];
  similarityPercentage: number;
  timestamp: number;
  surfHeight: string;
  rating: string;
  imageUrls: string[];
}

interface ForecastDataPoint {
  timestamp: number;
  closestMatches: ForecastMatches[];
}

type SimilarityResponse = ForecastDataPoint[];

interface Props {
  spotId: string;
  utcOffset: number;
  activeSurf: SurfTooltipData;
  graphWidth: number;
  isMobile: boolean;
  tide: TideData;
  wind: WindData;
  userUnits: Units;
}

const similarityFetch =
  (postBody: SwellSigRequest) =>
  (url: string): Promise<SimilarityResponse> =>
    doFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postBody),
    });

const SwellSignaturePoc: React.FC<Props> = ({
  spotId,
  utcOffset,
  activeSurf,
  graphWidth,
  isMobile,
  tide,
  wind,
  userUnits,
}) => {
  const imageRef = useRef<HTMLDivElement | null>(null);

  const positionStyles = useMemo(() => {
    const cardWidth = imageRef?.current?.offsetWidth || 0;
    let leftPosition = 0;
    if (activeSurf?.leftPosition >= 0) {
      leftPosition = Math.min(
        isMobile ? activeSurf.leftDayPosition : activeSurf.leftPosition - SURF_CARD_PADDING,
        graphWidth - cardWidth,
      );
    }
    return { left: Math.max(leftPosition, 0) };
  }, [activeSurf.leftDayPosition, activeSurf.leftPosition, graphWidth, isMobile]);

  const router = useRouter();
  const queryParams = router.query;

  const debugMode = queryParams?.swellSigPocDebug === 'true';

  const swellSigRequest = useMemo(
    () =>
      convertToSwellSigRequest({
        spotId,
        wind,
        tide,
        userUnits,
        queryParams,
      }),
    [wind, tide, userUnits, spotId, queryParams],
  );

  const {
    data,
    error,
    isValidating: isLoading,
  } = useSWR<SimilarityResponse>(
    swellSigRequest?.data ? `/science/similarity?` : null,
    similarityFetch({ ...swellSigRequest, queryParams } as SwellSigRequest),
    {
      errorRetryCount: 0,
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
    },
  );

  const matchesToShow = useMemo(() => {
    if (activeSurf.timestamp && data) {
      const forecast = data.find(
        (fdp: ForecastDataPoint) => fdp.timestamp === activeSurf.timestamp,
      );
      return forecast?.closestMatches.map((match) => ({
        ...match,
        timestamp: new Date(match.timestamp * 1000),
      }));
    }

    return [];
  }, [activeSurf.timestamp, data]);

  const images = useMemo(
    () =>
      Array(4)
        .fill(0)
        .map((_, index) => (
          <CamThumbImage
            key={`${activeSurf.timestamp}-${index}`}
            imageUrls={matchesToShow?.[index]?.imageUrls}
            timestamp={matchesToShow?.[index]?.timestamp}
            surfHeight={matchesToShow?.[index]?.surfHeight}
            rating={matchesToShow?.[index]?.rating}
            similarityPercentage={matchesToShow?.[index]?.similarityPercentage}
            utcOffset={utcOffset}
            debugMode={debugMode}
          />
        )),
    [matchesToShow, activeSurf.timestamp, utcOffset, debugMode],
  );

  if (isLoading) {
    return (
      <Box className={styles.swellSignaturePoc}>
        <LoadingSpinner />
      </Box>
    );
  }

  const renderErrorMessage = () => (
    <ErrorMessage message="Something went wrong loading similar condition images" />
  );

  return (
    <Box className={styles.swellSignaturePoc}>
      <ErrorBoundary
        error={error}
        errorBoundaryId="swell-signature-poc"
        render={renderErrorMessage}
      >
        {data ? (
          <div className={styles.textAndImageWrapper} style={positionStyles} ref={imageRef}>
            <div className={styles.swellSignaturePocImagesWrapper}>{images}</div>
          </div>
        ) : null}
      </ErrorBoundary>
    </Box>
  );
};

export default SwellSignaturePoc;
